import type { CitationReferenceData, MappingReferenceData, TokenReference } from 'services/reference-generator/types';
import type { ReviewItem } from 'store/useReviewStore';
import { addManualCitation, type ManualCitationMode } from './manualCitationAdd';
import { citationReviewStatus, isReviewItemActionable, mappingReviewStatus, reviewItemId } from './reviewAggregator';
import { isPendingMeta } from './securityAiPatches';

/** Fields the multi-cell citation planner needs from a grid cell. */
export type CitationTargetCell = {
  alias?: string;
  columnId?: string | number | null;
  key?: string;
  value?: unknown;
  isTitleCell?: boolean;
  isTitleOrHeader?: boolean;
  isTitleWithLabel?: boolean;
  isLegend?: boolean;
  isTotal?: boolean | null;
  expr?: string | null | number;
  citationReferences?: CitationReferenceData[];
  mappingReferences?: MappingReferenceData[];
  aiFieldMetadata?: unknown;
};

export type ManualCitationPayload = {
  documentReferenceId: number;
  tokenReference: TokenReference;
};

export type RejectedCitationTarget = {
  cell: CitationTargetCell;
  reason: string;
};

export type CellCitationPlan = {
  cell: CitationTargetCell;
  citationReferences: CitationReferenceData[];
  mappingReferences?: MappingReferenceData[];
  mutated: boolean;
};

export const sameRefs = <T>(left: T[] | undefined, right: T[]): boolean =>
  (left?.length ?? 0) === right.length && (left ?? []).every((item, index) => item === right[index]);

const planCells = (
  cells: CitationTargetCell[],
  nextRefs: (cell: CitationTargetCell) => CitationReferenceData[],
  nextMappings?: (cell: CitationTargetCell) => MappingReferenceData[]
): CellCitationPlan[] =>
  cells.map(cell => {
    const citationReferences = nextRefs(cell);
    const mappingReferences = nextMappings?.(cell);
    return {
      cell,
      citationReferences,
      ...(mappingReferences && { mappingReferences }),
      mutated:
        !sameRefs(cell.citationReferences, citationReferences) ||
        (mappingReferences != null && !sameRefs(cell.mappingReferences, mappingReferences)),
    };
  });

/** Review-item id for a security field cell. */
export const citationCellId = (cell: CitationTargetCell): string | null => {
  if (cell.columnId == null || cell.columnId === '' || !cell.alias) {
    return null;
  }
  return reviewItemId(Number(cell.columnId), cell.alias);
};

/** Why this cell cannot receive a PDF citation, or null when it can. */
export const citationTargetRejection = (cell: CitationTargetCell): string | null => {
  if (cell.isLegend || cell.isTitleCell || cell.isTitleOrHeader || cell.isTitleWithLabel) {
    return 'Table headings and labels cannot hold a citation';
  }
  if (cell.isTotal) {
    return 'Total cells cannot hold a citation';
  }
  if (!cell.alias) {
    return 'Row labels cannot hold a citation';
  }
  if (cell.columnId == null || cell.columnId === '') {
    return 'This cell is not a security field';
  }
  return null;
};

export const classifyCitationTargets = (
  cells: CitationTargetCell[]
): { supported: CitationTargetCell[]; rejected: RejectedCitationTarget[] } => {
  const supported: CitationTargetCell[] = [];
  const rejected: RejectedCitationTarget[] = [];
  cells.forEach(cell => {
    const reason = citationTargetRejection(cell);
    if (reason) {
      rejected.push({ cell, reason });
    } else {
      supported.push(cell);
    }
  });
  return { supported, rejected };
};

export const planApplyCitationToCells = (
  cells: CitationTargetCell[],
  payload: ManualCitationPayload,
  mode: ManualCitationMode = 'append'
): { applied: CellCitationPlan[]; rejected: RejectedCitationTarget[] } => {
  const { supported, rejected } = classifyCitationTargets(cells);
  return {
    applied: planCells(supported, cell =>
      addManualCitation(
        cell.citationReferences,
        {
          fieldName: cell.alias,
          documentReferenceId: payload.documentReferenceId,
          tokenReference: payload.tokenReference,
        },
        mode
      )
    ),
    rejected,
  };
};

export const planClearCitationsOnCells = (cells: CitationTargetCell[]): CellCitationPlan[] =>
  planCells(
    classifyCitationTargets(cells).supported,
    () => [],
    () => []
  );

/** Saved citation ids, once each, that Clear must delete through the API. */
export const persistedCitationIdsFromPlans = (plans: CellCitationPlan[]): number[] => [
  ...new Set(
    plans
      .filter(plan => plan.mutated)
      .flatMap(({ cell }) => (cell.citationReferences ?? []).map(ref => ref.id))
      .filter((id): id is number => typeof id === 'number')
  ),
];

/** Drop citations the API deleted; unsaved local rows and Excel refs clear without a round trip. */
export const restrictClearPlansToIds = (cells: CitationTargetCell[], succeededIds: Set<number>): CellCitationPlan[] =>
  planCells(
    cells,
    cell => (cell.citationReferences ?? []).filter(ref => ref.id != null && !succeededIds.has(ref.id)),
    () => []
  );

/** Cells that still hold any of the given citation ids (partial-failure reporting). */
export const cellsHoldingCitationIds = (cells: CitationTargetCell[], ids: Set<number>): CitationTargetCell[] =>
  cells.filter(cell => (cell.citationReferences ?? []).some(ref => typeof ref.id === 'number' && ids.has(ref.id)));

/** Review-panel cell ids for the selected security fields (filter only). */
export const reviewCellIdsForSelection = (cells: CitationTargetCell[]): string[] =>
  classifyCitationTargets(cells)
    .supported.map(citationCellId)
    .filter((id): id is string => id != null);

/** True when this cell still has a pending AI value, PDF citation, or Excel mapping. */
export const cellHasPendingSuggestion = (cell: CitationTargetCell): boolean => {
  if (isPendingMeta(cell.aiFieldMetadata)) {
    return true;
  }
  if ((cell.citationReferences ?? []).some(ref => citationReviewStatus(ref) === 'pending')) {
    return true;
  }
  return mappingReviewStatus(cell.mappingReferences) === 'pending';
};

/** Pending review cards that Accept or Reject settles for the selected cells. */
export const pendingReviewItemsForSelection = (cells: CitationTargetCell[], items: ReviewItem[]): ReviewItem[] => {
  const ids = new Set(reviewCellIdsForSelection(cells));
  return items.filter(item => ids.has(item.id) && item.status === 'pending' && isReviewItemActionable(item));
};
